import { useEffect } from 'react'
import SEO from '../components/SEO'
import SiteNav from '../components/SiteNav'
import Footer from '../components/Footer'
import { openContactModal } from '../components/ContactModal'

const SERVICES = [
  {
    label: '01 / AI API',
    title: 'Model APIs and gateway rails',
    body:
      'Provider routing, JSON contracts, eval hooks, retries, rate limits, logs, and spend controls for product teams shipping AI features.',
  },
  {
    label: '02 / AI infra',
    title: 'GPU and inference deployment',
    body:
      'Inference services, queue workers, model serving, autoscaling plans, observability, and cost-aware production environments.',
  },
  {
    label: '03 / Agents',
    title: 'Agentic workflows in production',
    body:
      'Claude Code, Codex, Cursor, and custom agents that inspect repos, call APIs, fix safe blockers, and return shipped work.',
  },
  {
    label: '04 / FDE',
    title: 'Embedded forward deployed teams',
    body:
      'A small senior team works inside your roadmap, turns ambiguous AI bets into shipped systems, and transfers the playbook back to your team.',
  },
]

const PARTNERS = [
  { name: 'Alibaba', logo: '/fde/partners/alibaba.svg', className: 'od-fde-partner-alibaba' },
  { name: 'miHoYo', logo: '/fde/partners/mihoyo.svg', className: 'od-fde-partner-mihoyo' },
  { name: 'UTS', logo: '/fde/partners/uts-logo.png', className: 'od-fde-partner-uts' },
  { name: 'UNSW', logo: '/fde/partners/unsw-logo.png', className: 'od-fde-partner-unsw' },
  { name: 'TikTok', logo: '/fde/partners/tiktok.svg', className: 'od-fde-partner-tiktok' },
  { name: '1Cyber', logo: '/fde/partners/1cyber-logo.png', className: 'od-fde-partner-1cyber' },
  { name: 'OpenAI', logo: '/fde/partners/openai.svg', className: 'od-fde-partner-openai' },
] as const

const DIFFERENTIATORS = [
  {
    label: 'FORWARD-DEPLOYED AI ENGINEERING',
    title: 'Builders embedded in your workflows',
    body:
      'Senior engineers work inside customer teams to scope the operational problem, design the AI workflow, and ship the first production path.',
    bullets: ['Workflow discovery', 'AI product design', 'Production implementation'],
  },
  {
    label: 'MANAGED AI API INFRASTRUCTURE',
    title: 'One governed layer for model-powered products',
    body:
      'TokenDance provides the API layer for model routing, JSON contracts, eval hooks, retries, logs, usage controls, and provider flexibility.',
    bullets: ['Model routing', 'Structured outputs', 'Logs and usage controls'],
  },
  {
    label: 'PRODUCTION OPERATIONS',
    title: 'AI systems that keep running after launch',
    body:
      'We harden deployments with observability, runbooks, cost controls, agent instructions, and the operating loop your team needs after handoff.',
    bullets: ['Deployment runbooks', 'Cost controls', 'Agent handoff'],
  },
] as const

const CASE_STUDIES = [
  {
    sector: 'SaaS · AI product launch',
    title: 'AI SaaS MVP to live product',
    summary:
      'Turning a prototype into a hosted AI product with auth, database, model API, logs, and a repeatable deploy loop.',
    metrics: [
      ['Week 1', 'First production slice'],
      ['1 API', 'Structured model gateway'],
      ['Live URL', 'Agent-operated deployment'],
    ],
    capabilities: ['AI API', 'Full-stack deployment', 'Agent handoff'],
  },
  {
    sector: 'Platform · Enterprise AI',
    title: 'Enterprise AI API gateway',
    summary:
      'Putting multiple model providers behind one governed API with JSON contracts, routing, observability, and cost controls.',
    metrics: [
      ['One gateway', 'OpenAI, Anthropic, Gemini, local models'],
      ['JSON mode', 'Fixed response contracts'],
      ['Usage view', 'Logs, spend, retries'],
    ],
    capabilities: ['Model routing', 'Governance', 'Cost visibility'],
  },
  {
    sector: 'Infra · GPU inference',
    title: 'GPU-backed inference service',
    summary:
      'Deploying model endpoints, queue workers, and background jobs with the resource profile and runbook needed for production.',
    metrics: [
      ['GPU ready', 'Inference endpoint and workers'],
      ['Queues', 'Async workload path'],
      ['Runbook', 'Operate after handoff'],
    ],
    capabilities: ['GPU infra', 'Workers', 'Observability'],
  },
] as const

const CASES = [
  {
    title: 'AI SaaS MVP to live product',
    body:
      'Turn a prototype into a hosted app with auth, database, env management, logs, deploy history, and a clean agent handoff.',
  },
  {
    title: 'Enterprise AI API gateway',
    body:
      'Put OpenAI, Anthropic, Gemini, and local models behind one governed API with structured responses and cost visibility.',
  },
  {
    title: 'GPU-backed inference service',
    body:
      'Deploy model endpoints, background jobs, and worker queues with the right resource profile and operational runbook.',
  },
  {
    title: 'Internal agent platform',
    body:
      'Give engineering, support, or operations teams agents that read docs, call tools, write reports, and leave auditable traces.',
  },
  {
    title: 'Knowledge and RAG workflows',
    body:
      'Build document pipelines, retrieval, permissions, evaluations, and feedback loops around real internal data.',
  },
  {
    title: 'Deployment rescue and migration',
    body:
      'Move AI apps off fragile scripts and one-off prototypes into a repeatable TokenDance operating loop your team can trust.',
  },
]

const PROCESS = [
  ['01', 'Scope the operational truth', 'We map the product goal, data shape, security boundary, model choices, deployment path, and success metric.'],
  ['02', 'Build the smallest production path', 'We ship one vertical slice through API, infra, evals, logs, and user workflow before widening scope.'],
  ['03', 'Harden with the team', 'We add tests, telemetry, runbooks, prompt storage, structured outputs, and failure handling around real usage.'],
  ['04', 'Transfer the system', 'Your team gets the code, architecture notes, agent instructions, and deployment loop needed to keep moving.'],
] as const

const FDE_TITLE = 'TokenDance FDE Teams - AI APIs, GPU Infra, and Production Agents'
const FDE_DESCRIPTION =
  'TokenDance partners with ambitious businesses to operationalize AI at scale. Our forward-deployed engineers work inside customer workflows to design, build, deploy, and operate AI systems, backed by our managed AI API layer and production infrastructure.'

const FDE_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': 'https://tokendance.net/fde#service',
  name: 'TokenDance FDE Teams',
  serviceType: 'Forward deployed engineering for production AI systems',
  provider: { '@id': 'https://tokendance.net/#organization' },
  areaServed: 'Global',
  url: 'https://tokendance.net/fde',
  description: FDE_DESCRIPTION,
}

function PartnerLogos({ duplicate = false }: { duplicate?: boolean }) {
  return (
    <div className="od-fde-partner-group" aria-hidden={duplicate ? 'true' : undefined}>
      {PARTNERS.map((partner) => (
        <span key={`${duplicate ? 'duplicate-' : ''}${partner.name}`} className={`od-fde-partner ${partner.className}`}>
          <img src={partner.logo} alt={duplicate ? '' : partner.name} loading="lazy" decoding="async" />
        </span>
      ))}
    </div>
  )
}

export default function Fde() {
  useEffect(() => {
    document.body.classList.add('v2-fde-page')
    return () => document.body.classList.remove('v2-fde-page')
  }, [])

  return (
    <>
      <SEO
        title={FDE_TITLE}
        description={FDE_DESCRIPTION}
        path="/fde"
        jsonLd={FDE_JSONLD}
      />

      <div className="od-home od-fde-shell">
        <SiteNav />

        <main className="od-fde-main">
          <section className="od-fde-hero" aria-labelledby="fde-title">
            <div className="od-fde-hero-copy">
              <p className="od-section-label">Forward deployed engineering</p>
              <h1 id="fde-title">We help businesses design, deploy, and operationalize their AI transformations.</h1>
              <div className="od-fde-actions" aria-label="FDE contact actions">
                <button type="button" className="od-fde-primary" onClick={openContactModal}>
                  Talk to FDE
                </button>
                <a className="od-fde-secondary" href="#cases">
                  View cases <span aria-hidden="true">-&gt;</span>
                </a>
              </div>
            </div>

            <aside className="od-fde-console" aria-label="TokenDance FDE workflow">
              <div className="od-fde-console-head">
                <span>Production path</span>
                <span>TokenDance FDE</span>
              </div>
              <ol className="od-fde-console-steps">
                <li>
                  <span>Business goal</span>
                  <code>revenue, operations, support, research, or internal tools</code>
                </li>
                <li>
                  <span>AI system</span>
                  <code>model API + data context + GPU service + agent workflow</code>
                </li>
                <li>
                  <span>Deployment</span>
                  <code>build, logs, resources, observability, and live URL</code>
                </li>
                <li>
                  <span>Operating loop</span>
                  <code>runbook, evals, cost controls, and agent instructions</code>
                </li>
              </ol>
            </aside>
          </section>

          <section className="od-fde-partners" aria-labelledby="fde-partners-title">
            <h2 id="fde-partners-title">Trusted by</h2>
            <div className="od-fde-partner-marquee" aria-label="Trusted partner logos">
              <div className="od-fde-partner-track">
                <PartnerLogos />
                <PartnerLogos duplicate />
              </div>
            </div>
          </section>

          <section className="od-fde-intro" aria-labelledby="fde-intro-title">
            <p id="fde-intro-title" className="od-section-label">Operationalize AI at scale</p>
            <p>
              TokenDance partners with ambitious businesses to operationalize AI at scale. Our
              forward-deployed engineers work inside customer workflows to design, build,
              deploy, and operate AI systems, backed by our managed AI API layer and
              production infrastructure.
            </p>
          </section>

          <section className="od-fde-different" aria-labelledby="fde-different-title">
            <div className="od-fde-section-head">
              <p className="od-section-label">What makes TokenDance different</p>
              <h2 id="fde-different-title">
                We combine the three layers customers need in one company: forward-deployed AI
                engineering, managed AI API infrastructure, and production operations.
              </h2>
            </div>
            <div className="od-fde-layer-stack">
              {DIFFERENTIATORS.map((item) => (
                <article key={item.title} className="od-fde-layer">
                  <div className="od-fde-layer-kicker">{item.label}</div>
                  <div className="od-fde-layer-copy">
                    <h3>{item.title}</h3>
                    <p>{item.body}</p>
                  </div>
                  <ul aria-label={`${item.title} includes`}>
                    {item.bullets.map((bullet) => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </section>

          <section className="od-fde-study-section" aria-labelledby="fde-study-title">
            <div className="od-fde-section-head">
              <p className="od-section-label">Case study patterns</p>
              <h2 id="fde-study-title">The shape of the engagements we run.</h2>
            </div>
            <div className="od-fde-study-grid">
              {CASE_STUDIES.map((study) => (
                <article key={study.title} className="od-fde-study-card">
                  <header>
                    <span>{study.sector}</span>
                    <h3>{study.title}</h3>
                    <p>{study.summary}</p>
                  </header>
                  <div className="od-fde-study-metrics" aria-label={`${study.title} metrics`}>
                    {study.metrics.map(([value, label]) => (
                      <div key={value}>
                        <strong>{value}</strong>
                        <span>{label}</span>
                      </div>
                    ))}
                  </div>
                  <div className="od-fde-study-tags" aria-label={`${study.title} capabilities`}>
                    {study.capabilities.map((capability) => (
                      <span key={capability}>{capability}</span>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="od-fde-services" aria-label="FDE capabilities">
            {SERVICES.map((service) => (
              <article key={service.title} className="od-fde-service">
                <span>{service.label}</span>
                <h2>{service.title}</h2>
                <p>{service.body}</p>
              </article>
            ))}
          </section>

          <section className="od-fde-process" aria-labelledby="fde-process-title">
            <div>
              <p className="od-section-label">How we work</p>
              <h2 id="fde-process-title">From vague AI plan to a system with owners.</h2>
            </div>
            <ol>
              {PROCESS.map(([n, title, body]) => (
                <li key={n}>
                  <span>{n}</span>
                  <div>
                    <h3>{title}</h3>
                    <p>{body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <section id="cases" className="od-fde-cases" aria-labelledby="fde-cases-title">
            <div className="od-fde-section-head">
              <p className="od-section-label">Cases we do</p>
              <h2 id="fde-cases-title">The work we are useful for.</h2>
            </div>
            <div className="od-fde-case-grid">
              {CASES.map((item) => (
                <article key={item.title} className="od-fde-case">
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </article>
              ))}
            </div>
          </section>
        </main>
      </div>

      <Footer />
    </>
  )
}
